import { can } from '@clim/client-core';
import {
  AUTO_RULES,
  AUTO_RULE_LABELS,
  ENERGY_CLASSES,
  SECTION_KIND_LABELS,
  UNIT_LABELS,
  currentPrice,
  formatEuros,
  formatRateBp,
  missingForSale,
  ttcCents,
  htOfTtc,
  priceTtcOf,
  compareAtTtcOf,
  type CatalogBrandRecord,
  type CatalogFamilyRecord,
  type CatalogItemRecord,
  type CatalogSectionRecord,
  type DossierTypeRecord,
  type SectionKind,
  type TerritoryRecord,
} from '@clim/domain';
import { useMemo, useState } from 'react';
import { Empty, ErrorBox, Field, Input, Modal, Select, Textarea } from '../../components/ui';
import { useReady } from '../../lib/session';
import { useApi, useMutation } from './common';

interface Catalog {
  sections: CatalogSectionRecord[];
  families: CatalogFamilyRecord[];
  brands: CatalogBrandRecord[];
  items: CatalogItemRecord[];
}

/** « 1 234,56 » → 123456 centimes ; null si vide ou invalide. */
export function parseEuros(v: string): number | null {
  const n = Number(v.replace(/\s/g, '').replace(',', '.'));
  return v.trim() === '' || !Number.isFinite(n) || n < 0 ? null : Math.round(n * 100);
}
const parsePercentBp = (v: string) => {
  const n = Number(v.replace(',', '.'));
  return v.trim() === '' || !Number.isFinite(n) || n < 0 ? null : Math.round(n * 100);
};
const power = (i: CatalogItemRecord) =>
  i.powerMinBtu == null
    ? ''
    : i.powerMinBtu === i.powerMaxBtu || i.powerMaxBtu == null
      ? `${i.powerMinBtu.toLocaleString('fr-FR')} BTU`
      : `${i.powerMinBtu.toLocaleString('fr-FR')}–${i.powerMaxBtu.toLocaleString('fr-FR')} BTU`;

/**
 * Catalogue entièrement administrable (CDC §6) : aucune valeur imposée par l'application.
 * Structure → articles → tarifs (versions datées, historique conservé).
 */
export function CatalogAdmin() {
  const catalog = useApi<Catalog>('/v1/catalog');
  const types = useApi<DossierTypeRecord[]>('/v1/dossier-types');
  const territories = useApi<TerritoryRecord[]>('/v1/territories');
  const [sectionId, setSectionId] = useState<string | null>(null);
  const [editing, setEditing] = useState<CatalogItemRecord | 'new' | null>(null);
  const [pricing, setPricing] = useState<CatalogItemRecord | null>(null);
  const [structure, setStructure] = useState(false);
  const [importing, setImporting] = useState(false);
  const exporter = useMutation();
  const data = catalog.data;
  const section = data?.sections.find((s) => s.id === sectionId) ?? data?.sections[0];
  const items = useMemo(
    () =>
      (data?.items ?? [])
        .filter((i) => i.sectionId === section?.id)
        .sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label)),
    [data, section],
  );
  const activeTerritories = (territories.data ?? []).filter((t) => t.isActive);

  return (
    <div>
      <ErrorBox message={catalog.error} />
      <div className="page-head">
        <div className="chips">
          {data?.sections.map((s) => (
            <button
              key={s.id}
              className={`chip${section?.id === s.id ? ' on' : ''}${s.isActive ? '' : ' muted'}`}
              onClick={() => setSectionId(s.id)}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div className="actions">
          <button className="btn small" onClick={() => setStructure(true)}>
            Sections, familles, marques
          </button>
          <button className="btn small" onClick={() => setImporting(true)}>
            Importer un tableur
          </button>
          <button
            className="btn small"
            onClick={async () => {
              const csv = (await exporter.run('GET', '/v1/admin/catalog/export')) as string;
              downloadText(csv, `catalogue-${new Date().toISOString().slice(0, 10)}.csv`);
            }}
          >
            Exporter
          </button>
        </div>
      </div>
      <ErrorBox message={exporter.error} />
      {section && (
        <>
          <div className="page-head">
            <h2>
              {section.label} <span className="muted">· {SECTION_KIND_LABELS[section.kind]}</span>
            </h2>
            <button className="btn primary" onClick={() => setEditing('new')}>
              + Article
            </button>
          </div>
          {items.length === 0 ? (
            <Empty>Aucun article. Ajoutez-en avec « + Article », puis définissez son tarif.</Empty>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Article</th>
                  <th>Puissance</th>
                  {activeTerritories.map((t) => (
                    <th key={t.id}>Prix HT {t.code}</th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                  <tr key={i.id} className={i.isActive ? '' : 'disabled'}>
                    <td>
                      <strong>{i.label}</strong>
                      <div className="muted">
                        {[
                          data?.families.find((f) => f.id === i.familyId)?.label,
                          data?.brands.find((b) => b.id === i.brandId)?.label,
                          i.model,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                        {i.optionSteps.length > 0 && ` · ${i.optionSteps.length} étape(s)`}
                      </div>
                    </td>
                    <td className="nowrap">{power(i)}</td>
                    {activeTerritories.map((t) => {
                      const p = currentPrice(i.prices, t.id);
                      return (
                        <td key={t.id} className="nowrap">
                          {p ? `${formatEuros(priceTtcOf(p))} TTC` : <span className="field-error">sans tarif</span>}
                        </td>
                      );
                    })}
                    <td className="nowrap">
                      <button className="btn small" onClick={() => setEditing(i)}>
                        Modifier
                      </button>
                      <button className="btn small" onClick={() => setPricing(i)}>
                        Tarifs
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
      {importing && (
        <ImportDialog
          onClose={(changed) => {
            setImporting(false);
            if (changed) catalog.reload();
          }}
        />
      )}
      {editing && data && (
        <ItemDialog
          item={editing === 'new' ? null : editing}
          sectionId={section?.id ?? ''}
          catalog={data}
          types={types.data ?? []}
          territories={activeTerritories}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            catalog.reload();
          }}
        />
      )}
      {pricing && (
        <PricesDialog
          item={pricing}
          territories={activeTerritories}
          onClose={() => {
            setPricing(null);
            catalog.reload();
          }}
        />
      )}
      {structure && data && (
        <StructureDialog
          catalog={data}
          onClose={() => {
            setStructure(false);
            catalog.reload();
          }}
        />
      )}
    </div>
  );
}

function ItemDialog(props: {
  item: CatalogItemRecord | null;
  sectionId: string;
  catalog: Catalog;
  types: DossierTypeRecord[];
  territories: TerritoryRecord[];
  onClose(): void;
  onSaved(): void;
}) {
  const { item, catalog } = props;
  const m = useMutation();
  const [f, setF] = useState({
    sectionId: item?.sectionId ?? props.sectionId,
    familyId: item?.familyId ?? '',
    brandId: item?.brandId ?? '',
    label: item?.label ?? '',
    model: item?.model ?? '',
    description: item?.description ?? '',
    unit: item?.unit ?? 'unit',
    powerMinBtu: item?.powerMinBtu?.toString() ?? '',
    powerMaxBtu: item?.powerMaxBtu?.toString() ?? '',
    dossierTypeIds: item?.dossierTypeIds ?? [],
    territoryIds: item?.territoryIds ?? [],
    stockMode: item?.stockMode ?? 'unlimited',
    steps: (item?.optionSteps ?? []).join('\n'),
    energyClass: item?.energyClass ?? '',
    warranty: item?.warranty ?? '',
    autoRule: item?.autoRule ?? '',
    requiresItemIds: item?.requiresItemIds ?? [],
    quoteHighlight: item?.quoteHighlight ?? false,
    sortOrder: item?.sortOrder ?? 0,
    isActive: item?.isActive ?? true,
  });
  const toggle = (key: 'dossierTypeIds' | 'territoryIds', id: string) =>
    setF({ ...f, [key]: f[key].includes(id) ? f[key].filter((x) => x !== id) : [...f[key], id] });
  const families = catalog.families.filter(
    (x) => x.sectionId === f.sectionId && (x.isActive || x.id === f.familyId),
  );
  const save = async () => {
    const body = {
      sectionId: f.sectionId,
      familyId: f.familyId || null,
      brandId: f.brandId || null,
      label: f.label,
      model: f.model || null,
      description: f.description || null,
      unit: f.unit,
      powerMinBtu: f.powerMinBtu ? Number(f.powerMinBtu) : null,
      powerMaxBtu: f.powerMaxBtu ? Number(f.powerMaxBtu) : f.powerMinBtu ? Number(f.powerMinBtu) : null,
      dossierTypeIds: f.dossierTypeIds,
      territoryIds: f.territoryIds,
      stockMode: f.stockMode,
      optionSteps: f.steps
        .split('\n')
        .map((x) => x.trim())
        .filter(Boolean),
      energyClass: f.energyClass || null,
      warranty: f.warranty.trim() || null,
      autoRule: f.autoRule || null,
      requiresItemIds: f.requiresItemIds,
      quoteHighlight: f.quoteHighlight,
      sortOrder: Number(f.sortOrder) || 0,
      ...(item ? { isActive: f.isActive } : {}),
    };
    if (item) await m.run('PATCH', `/v1/admin/catalog/items/${item.id}`, body);
    else await m.run('POST', '/v1/admin/catalog/items', body);
    props.onSaved();
  };
  return (
    <Modal title={item ? item.label : 'Nouvel article'} onClose={props.onClose}>
      <div className="form">
        <div className="grid2">
          <Field label="Section">
            <Select
              value={f.sectionId}
              onChange={(e) => setF({ ...f, sectionId: e.target.value, familyId: '' })}
            >
              {catalog.sections.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Famille / type">
            <Select value={f.familyId} onChange={(e) => setF({ ...f, familyId: e.target.value })}>
              <option value="">—</option>
              {families.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.label}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <Field label="Libellé">
          <Input
            value={f.label}
            onChange={(e) => setF({ ...f, label: e.target.value })}
            placeholder="Ex. : Split mural 12 000 BTU"
          />
        </Field>
        <div className="grid2">
          <Field label="Marque">
            <Select value={f.brandId} onChange={(e) => setF({ ...f, brandId: e.target.value })}>
              <option value="">—</option>
              {catalog.brands
                .filter((b) => b.isActive || b.id === f.brandId)
                .map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.label}
                  </option>
                ))}
            </Select>
          </Field>
          <Field label="Variante / modèle (référence exacte)">
            <Input value={f.model} onChange={(e) => setF({ ...f, model: e.target.value })} />
          </Field>
        </div>
        <div className="grid2">
          <Field label="Classe énergétique">
            <Select value={f.energyClass} onChange={(e) => setF({ ...f, energyClass: e.target.value })}>
              <option value="">—</option>
              {ENERGY_CLASSES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          </Field>
          <Field
            label="Garantie (propre à ce modèle)"
            hint="Telle que fournie par le fabricant, ex. : 3 ans pièces, 5 ans compresseur"
          >
            <Input value={f.warranty} onChange={(e) => setF({ ...f, warranty: e.target.value })} />
          </Field>
        </div>
        {f.brandId &&
          missingForSale({
            brandId: f.brandId,
            model: f.model || null,
            energyClass: f.energyClass || null,
            warranty: f.warranty.trim() || null,
          }).length > 0 && (
            <p className="muted">
              ⚠️ Appareil avec marque : modèle, classe énergétique et garantie sont obligatoires pour être
              ajouté à un devis.
            </p>
          )}
        <Field
          label="Ajout automatique au dossier"
          hint="Ex. nacelle : ajoutée d’office quand le commercial choisit « Nacelle » (et retirée sinon)"
        >
          <Select value={f.autoRule} onChange={(e) => setF({ ...f, autoRule: e.target.value as typeof f.autoRule })}>
            <option value="">Jamais (choisi par le commercial)</option>
            {AUTO_RULES.map((r) => (
              <option key={r} value={r}>
                {AUTO_RULE_LABELS[r]}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Exige dans le même dossier"
          hint="Option liée : ex. l’extension de garantie 5 ans exige le contrat d’entretien 5 ans (ajouté d’office, contrôlé à la soumission et à la signature)"
        >
          <Select
            value=""
            onChange={(e) => e.target.value && setF({ ...f, requiresItemIds: [...f.requiresItemIds, e.target.value] })}
          >
            <option value="">+ Ajouter un article exigé…</option>
            {catalog.items
              .filter((x) => x.id !== item?.id && !f.requiresItemIds.includes(x.id))
              .map((x) => (
                <option key={x.id} value={x.id}>
                  {x.label}
                </option>
              ))}
          </Select>
        </Field>
        {f.requiresItemIds.length > 0 && (
          <ul className="plain">
            {f.requiresItemIds.map((rid) => (
              <li key={rid} className="row">
                <span className="row-main">Exige : {catalog.items.find((x) => x.id === rid)?.label ?? '?'}</span>
                <button
                  type="button"
                  className="btn small ghost"
                  aria-label="Retirer l’exigence"
                  onClick={() => setF({ ...f, requiresItemIds: f.requiresItemIds.filter((x) => x !== rid) })}
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        )}
        <label className="check">
          <input type="checkbox" checked={f.quoteHighlight} onChange={(e) => setF({ ...f, quoteHighlight: e.target.checked })} />
          Mettre en avant sur le devis et le bon de commande (encadré « Garantie et entretien »)
        </label>
        {item && <ItemImage item={item} onChanged={props.onSaved} />}
        <div className="grid3">
          <Field label="Unité">
            <Select value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value as typeof f.unit })}>
              {Object.entries(UNIT_LABELS).map(([k, l]) => (
                <option key={k} value={k}>
                  {l}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Puissance (BTU) min" hint="vide si sans objet">
            <Input
              type="number"
              value={f.powerMinBtu}
              onChange={(e) => setF({ ...f, powerMinBtu: e.target.value })}
            />
          </Field>
          <Field label="Puissance max (tranche)">
            <Input
              type="number"
              value={f.powerMaxBtu}
              onChange={(e) => setF({ ...f, powerMaxBtu: e.target.value })}
            />
          </Field>
        </div>
        <Field label="Description">
          <Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
        </Field>
        <Field
          label="Étapes (options : une par ligne)"
          hint="Affichées dépliées dans le dossier, le récapitulatif et le contrat"
        >
          <Textarea value={f.steps} onChange={(e) => setF({ ...f, steps: e.target.value })} rows={4} />
        </Field>
        <fieldset>
          <legend>Types de dossier (aucun coché = tous)</legend>
          {props.types.map((t) => (
            <label key={t.id} className="check">
              <input
                type="checkbox"
                checked={f.dossierTypeIds.includes(t.id)}
                onChange={() => toggle('dossierTypeIds', t.id)}
              />{' '}
              {t.label}
            </label>
          ))}
        </fieldset>
        <fieldset>
          <legend>Territoires (aucun coché = tous)</legend>
          {props.territories.map((t) => (
            <label key={t.id} className="check">
              <input
                type="checkbox"
                checked={f.territoryIds.includes(t.id)}
                onChange={() => toggle('territoryIds', t.id)}
              />{' '}
              {t.name}
            </label>
          ))}
        </fieldset>
        <div className="grid2">
          <Field label="Stock">
            <Select
              value={f.stockMode}
              onChange={(e) => setF({ ...f, stockMode: e.target.value as typeof f.stockMode })}
            >
              <option value="unlimited">Illimité (jamais décrémenté)</option>
              <option value="stocked">Stocké (module Stock)</option>
            </Select>
          </Field>
          <Field label="Ordre d’affichage">
            <Input
              type="number"
              value={f.sortOrder}
              onChange={(e) => setF({ ...f, sortOrder: Number(e.target.value) })}
            />
          </Field>
        </div>
        {item && (
          <label className="check">
            <input
              type="checkbox"
              checked={f.isActive}
              onChange={(e) => setF({ ...f, isActive: e.target.checked })}
            />{' '}
            Article actif
          </label>
        )}
        <ErrorBox message={m.error} />
        <div className="actions">
          <button className="btn" onClick={props.onClose}>
            Annuler
          </button>
          <button
            className="btn primary"
            disabled={!f.label.trim()}
            onClick={() => void save().catch(() => undefined)}
          >
            Enregistrer
          </button>
        </div>
      </div>
    </Modal>
  );
}

interface PriceHistoryRow {
  id: string;
  territoryId: string | null;
  validFrom: string;
  priceHtCents: number;
  compareAtPriceHtCents?: number | null;
  priceTtcCents?: number | null;
  compareAtPriceTtcCents?: number | null;
  vatRateBp: number;
  saleCommissionType: 'none' | 'fixed' | 'percent' | null;
  saleCommissionValue: number | null;
  installRemunerationCents: number | null;
  note: string | null;
  createdAt: string;
  createdBy: string | null;
}

function commissionLabel(p: Pick<PriceHistoryRow, 'saleCommissionType' | 'saleCommissionValue'>) {
  if (p.saleCommissionType === null) return '—';
  if (p.saleCommissionType === 'fixed') return `${formatEuros(p.saleCommissionValue)} / unité`;
  if (p.saleCommissionType === 'percent') return formatRateBp(p.saleCommissionValue ?? 0);
  return 'aucune';
}

/** Tarifs : on n'écrase jamais, on ajoute une version (immédiate ou datée). */
function PricesDialog({
  item,
  territories,
  onClose,
}: {
  item: CatalogItemRecord;
  territories: TerritoryRecord[];
  onClose(): void;
}) {
  const { me } = useReady();
  const history = useApi<PriceHistoryRow[]>(`/v1/admin/catalog/items/${item.id}/prices`);
  const m = useMutation();
  const rules = can(me, 'commission.rules.manage');
  const last = history.data?.[0];
  const [f, setF] = useState({
    territoryId: '',
    /** prix saisis TTC (ce que le client paie, au centime près) ou HT */
    basis: 'ttc' as 'ttc' | 'ht',
    price: '',
    oldPrice: '',
    vat: '8,5',
    commissionType: 'none' as 'none' | 'fixed' | 'percent',
    commission: '',
    remuneration: '',
    when: 'now' as 'now' | 'date',
    validFrom: '',
    note: '',
  });
  const [formError, setFormError] = useState<string | null>(null);
  const otherBasis = (cents: number) =>
    f.basis === 'ttc' ? htOfTtc(cents, parsePercentBp(f.vat)!) : ttcCents(cents, parsePercentBp(f.vat)!);
  const tName = (id: string | null) =>
    id ? (territories.find((t) => t.id === id)?.name ?? '?') : 'Tous territoires';

  const prefill = () => {
    if (!last) return;
    setF({
      ...f,
      basis: 'ttc',
      price: (priceTtcOf({ ...last, priceTtcCents: last.priceTtcCents ?? null }) / 100)
        .toString()
        .replace('.', ','),
      oldPrice:
        last.compareAtPriceHtCents != null
          ? (
              compareAtTtcOf({
                ...last,
                compareAtPriceHtCents: last.compareAtPriceHtCents,
                compareAtPriceTtcCents: last.compareAtPriceTtcCents ?? null,
              })! / 100
            )
              .toString()
              .replace('.', ',')
          : '',
      vat: (last.vatRateBp / 100).toString().replace('.', ','),
      commissionType: last.saleCommissionType ?? 'none',
      commission:
        last.saleCommissionType === 'fixed'
          ? ((last.saleCommissionValue ?? 0) / 100).toString().replace('.', ',')
          : last.saleCommissionType === 'percent'
            ? ((last.saleCommissionValue ?? 0) / 100).toString().replace('.', ',')
            : '',
      remuneration:
        last.installRemunerationCents != null
          ? (last.installRemunerationCents / 100).toString().replace('.', ',')
          : '',
    });
  };

  const save = async () => {
    setFormError(null);
    const price = parseEuros(f.price);
    const vatRateBp = parsePercentBp(f.vat);
    if (price === null || vatRateBp === null) return setFormError('Prix et TVA obligatoires');
    const old = f.oldPrice.trim() ? parseEuros(f.oldPrice) : null;
    const ttc = f.basis === 'ttc';
    const body: Record<string, unknown> = {
      territoryId: f.territoryId || null,
      priceHtCents: ttc ? htOfTtc(price, vatRateBp) : price,
      compareAtPriceHtCents: old === null ? null : ttc ? htOfTtc(old, vatRateBp) : old,
      // TTC exact : le client paie exactement le montant saisi, sans écart d'un centime
      ...(ttc ? { priceTtcCents: price, compareAtPriceTtcCents: old } : {}),
      vatRateBp,
      note: f.note || null,
      ...(f.when === 'date' && f.validFrom ? { validFrom: new Date(f.validFrom).toISOString() } : {}),
    };
    if (rules) {
      body.saleCommissionType = f.commissionType;
      body.saleCommissionValue =
        f.commissionType === 'fixed'
          ? (parseEuros(f.commission) ?? 0)
          : f.commissionType === 'percent'
            ? (parsePercentBp(f.commission) ?? 0)
            : 0;
      body.installRemunerationCents = f.remuneration.trim() ? parseEuros(f.remuneration) : null;
    }
    await m.run('POST', `/v1/admin/catalog/items/${item.id}/prices`, body);
    history.reload();
    setF({
      ...f,
      price: '',
      oldPrice: '',
      commission: '',
      remuneration: '',
      note: '',
      when: 'now',
      validFrom: '',
    });
  };

  return (
    <Modal title={`Tarifs — ${item.label}`} onClose={onClose}>
      <div className="form">
        <p className="muted">
          Chaque modification crée une nouvelle version datée ; les dossiers existants conservent leur prix,
          et les commissions sont figées à la validation.
        </p>
        <div className="grid2">
          <Field label="Territoire">
            <Select value={f.territoryId} onChange={(e) => setF({ ...f, territoryId: e.target.value })}>
              <option value="">Tous territoires (par défaut)</option>
              {territories.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Prise d’effet">
            <Select value={f.when} onChange={(e) => setF({ ...f, when: e.target.value as 'now' | 'date' })}>
              <option value="now">Immédiate</option>
              <option value="date">À une date</option>
            </Select>
          </Field>
        </div>
        {f.when === 'date' && (
          <Field label="Date d’effet">
            <Input
              type="datetime-local"
              value={f.validFrom}
              onChange={(e) => setF({ ...f, validFrom: e.target.value })}
            />
          </Field>
        )}
        <Field label="Prix saisis">
          <Select value={f.basis} onChange={(e) => setF({ ...f, basis: e.target.value as 'ttc' | 'ht' })}>
            <option value="ttc">TTC (prix payé par le client, exact au centime)</option>
            <option value="ht">HT</option>
          </Select>
        </Field>
        <div className="grid2">
          <Field label={f.basis === 'ttc' ? 'Prix client TTC (€)' : 'Prix client HT (€)'}>
            <Input
              value={f.price}
              onChange={(e) => setF({ ...f, price: e.target.value })}
              inputMode="decimal"
            />
          </Field>
          <Field label="TVA (%)">
            <Input value={f.vat} onChange={(e) => setF({ ...f, vat: e.target.value })} inputMode="decimal" />
          </Field>
        </div>
        <Field label={`Ancien prix ${f.basis === 'ttc' ? 'TTC' : 'HT'} (€) — affiché barré, facultatif`}>
          <Input
            value={f.oldPrice}
            onChange={(e) => setF({ ...f, oldPrice: e.target.value })}
            inputMode="decimal"
          />
        </Field>
        {parseEuros(f.price) !== null && parsePercentBp(f.vat) !== null && (
          <p className="muted">
            {f.basis === 'ttc' ? 'Prix HT correspondant' : 'Prix client TTC'} :{' '}
            <strong>{formatEuros(otherBasis(parseEuros(f.price)!))}</strong>
            {parseEuros(f.oldPrice) !== null && f.oldPrice.trim() && (
              <>
                {' '}
                · ancien prix barré : <s>{formatEuros(otherBasis(parseEuros(f.oldPrice)!))}</s>
              </>
            )}
          </p>
        )}
        {rules ? (
          <div className="grid2">
            <Field label="Commission de vente">
              <Select
                value={f.commissionType}
                onChange={(e) => setF({ ...f, commissionType: e.target.value as typeof f.commissionType })}
              >
                <option value="none">Aucune</option>
                <option value="fixed">Montant par unité (€)</option>
                <option value="percent">Pourcentage du HT (%)</option>
              </Select>
            </Field>
            {f.commissionType !== 'none' && (
              <Field label={f.commissionType === 'fixed' ? 'Commission (€ / unité)' : 'Commission (%)'}>
                <Input
                  value={f.commission}
                  onChange={(e) => setF({ ...f, commission: e.target.value })}
                  inputMode="decimal"
                />
              </Field>
            )}
            <Field label="Rémunération pose / entretien (€ / unité)" hint="vide = aucune">
              <Input
                value={f.remuneration}
                onChange={(e) => setF({ ...f, remuneration: e.target.value })}
                inputMode="decimal"
              />
            </Field>
          </div>
        ) : (
          <p className="muted">
            Les barèmes de commission et de rémunération en vigueur sont conservés (droit « barèmes » requis
            pour les modifier).
          </p>
        )}
        <Field label="Note (motif du changement)">
          <Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
        </Field>
        <ErrorBox message={formError ?? m.error} />
        <div className="actions">
          {last && (
            <button className="btn" onClick={prefill}>
              Reprendre le dernier tarif
            </button>
          )}
          <button className="btn primary" onClick={() => void save().catch(() => undefined)}>
            Ajouter cette version
          </button>
        </div>
        <h3>Historique</h3>
        {!history.data?.length ? (
          <Empty>Aucun tarif : l’article ne peut pas encore être vendu.</Empty>
        ) : (
          <table className="table compact">
            <thead>
              <tr>
                <th>Effet</th>
                <th>Territoire</th>
                <th>Prix TTC (HT)</th>
                <th>TVA</th>
                <th>Commission</th>
                <th>Rémunération</th>
                <th>Par</th>
              </tr>
            </thead>
            <tbody>
              {history.data.map((p) => (
                <tr key={p.id} className={new Date(p.validFrom) > new Date() ? 'planned' : ''}>
                  <td className="nowrap">
                    {new Date(p.validFrom).toLocaleDateString('fr-FR')}
                    {new Date(p.validFrom) > new Date() && ' (à venir)'}
                  </td>
                  <td>{tName(p.territoryId)}</td>
                  <td className="nowrap">
                    {formatEuros(p.priceTtcCents ?? ttcCents(p.priceHtCents, p.vatRateBp))}
                    {p.compareAtPriceHtCents != null && (
                      <>
                        {' '}
                        <s className="muted">
                          {formatEuros(p.compareAtPriceTtcCents ?? ttcCents(p.compareAtPriceHtCents, p.vatRateBp))}
                        </s>
                      </>
                    )}
                    <div className="muted small">{formatEuros(p.priceHtCents)} HT</div>
                  </td>
                  <td>{formatRateBp(p.vatRateBp)}</td>
                  <td className="nowrap">{commissionLabel(p)}</td>
                  <td className="nowrap">
                    {p.installRemunerationCents == null ? '—' : formatEuros(p.installRemunerationCents)}
                  </td>
                  <td>
                    {p.createdBy}
                    {p.note && <div className="muted">{p.note}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Modal>
  );
}

function StructureDialog({ catalog, onClose }: { catalog: Catalog; onClose(): void }) {
  const m = useMutation();
  const [sec, setSec] = useState({ label: '', kind: 'equipment' as SectionKind });
  const [fam, setFam] = useState({ sectionId: catalog.sections[0]?.id ?? '', label: '' });
  const [brand, setBrand] = useState('');
  const slug = (v: string) =>
    v
      .toLowerCase()
      .normalize('NFD')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '');
  const toggle = (path: string, isActive: boolean) =>
    void m.run('PATCH', path, { isActive: !isActive }).then(onClose, () => undefined);
  return (
    <Modal title="Structure du catalogue" onClose={onClose}>
      <div className="form">
        <ErrorBox message={m.error} />
        <h3>Sections</h3>
        <ul className="plain">
          {catalog.sections.map((s) => (
            <li key={s.id} className="row">
              <span className="row-main">
                {s.label} <span className="muted">({SECTION_KIND_LABELS[s.kind]})</span>
              </span>
              <label className="check">
                <input
                  type="checkbox"
                  checked={s.isActive}
                  onChange={() => toggle(`/v1/admin/catalog/sections/${s.id}`, s.isActive)}
                />{' '}
                active
              </label>
            </li>
          ))}
        </ul>
        <div className="grid3">
          <Field label="Nouvelle section">
            <Input value={sec.label} onChange={(e) => setSec({ ...sec, label: e.target.value })} />
          </Field>
          <Field label="Nature">
            <Select
              value={sec.kind}
              onChange={(e) => setSec({ ...sec, kind: e.target.value as SectionKind })}
            >
              {Object.entries(SECTION_KIND_LABELS).map(([k, l]) => (
                <option key={k} value={k}>
                  {l}
                </option>
              ))}
            </Select>
          </Field>
          <button
            className="btn"
            disabled={!sec.label}
            onClick={() =>
              void m
                .run('POST', '/v1/admin/catalog/sections', {
                  code: slug(sec.label),
                  label: sec.label,
                  kind: sec.kind,
                })
                .then(onClose, () => undefined)
            }
          >
            Ajouter
          </button>
        </div>
        <h3>Familles / types</h3>
        {catalog.sections.map((s) => (
          <div key={s.id}>
            <strong>{s.label}</strong>
            <ul className="plain">
              {catalog.families
                .filter((f) => f.sectionId === s.id)
                .map((f) => (
                  <li key={f.id} className="row">
                    <span className="row-main">{f.label}</span>
                    <label className="check">
                      <input
                        type="checkbox"
                        checked={f.isActive}
                        onChange={() => toggle(`/v1/admin/catalog/families/${f.id}`, f.isActive)}
                      />{' '}
                      active
                    </label>
                  </li>
                ))}
            </ul>
          </div>
        ))}
        <div className="grid3">
          <Field label="Section">
            <Select value={fam.sectionId} onChange={(e) => setFam({ ...fam, sectionId: e.target.value })}>
              {catalog.sections.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Nouvelle famille">
            <Input value={fam.label} onChange={(e) => setFam({ ...fam, label: e.target.value })} />
          </Field>
          <button
            className="btn"
            disabled={!fam.label}
            onClick={() =>
              void m.run('POST', '/v1/admin/catalog/families', fam).then(onClose, () => undefined)
            }
          >
            Ajouter
          </button>
        </div>
        <h3>Marques / variantes</h3>
        <ul className="plain">
          {catalog.brands.map((b) => (
            <li key={b.id} className="row">
              <span className="row-main">{b.label}</span>
              <label className="check">
                <input
                  type="checkbox"
                  checked={b.isActive}
                  onChange={() => toggle(`/v1/admin/catalog/brands/${b.id}`, b.isActive)}
                />{' '}
                active
              </label>
            </li>
          ))}
        </ul>
        <div className="inline">
          <Input
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
            placeholder="Nouvelle marque"
            aria-label="Nouvelle marque"
          />
          <button
            className="btn"
            disabled={!brand}
            onClick={() =>
              void m.run('POST', '/v1/admin/catalog/brands', { label: brand }).then(onClose, () => undefined)
            }
          >
            Ajouter
          </button>
        </div>
      </div>
    </Modal>
  );
}

function downloadText(text: string, filename: string) {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

interface ImportReport {
  applied: boolean;
  summary: {
    rows: number;
    created: number;
    updated: number;
    unchanged: number;
    errors: number;
    newPrices: number;
  };
  rows: {
    line: number;
    label: string;
    action: 'create' | 'update' | 'unchanged' | 'error';
    priceAction: 'new_version' | 'unchanged' | 'none';
    messages: string[];
  }[];
}
const IMPORT_ACTION: Record<ImportReport['rows'][number]['action'], string> = {
  create: 'Nouveau',
  update: 'Modifié',
  unchanged: 'Inchangé',
  error: 'Erreur',
};

/** Import tableur : aperçu à blanc, puis application tout-ou-rien. */
function ImportDialog({ onClose }: { onClose(changed: boolean): void }) {
  const m = useMutation();
  const [csv, setCsv] = useState<string | null>(null);
  const [fileName, setFileName] = useState('');
  const [report, setReport] = useState<ImportReport | null>(null);
  const [busy, setBusy] = useState(false);

  const preview = async (text: string) => {
    setBusy(true);
    setReport(null);
    try {
      setReport(
        (await m.run('POST', '/v1/admin/catalog/import', { csv: text, apply: false })) as ImportReport,
      );
    } catch {
      /* shown by m.error */
    } finally {
      setBusy(false);
    }
  };
  const apply = async () => {
    if (!csv) return;
    setBusy(true);
    try {
      setReport((await m.run('POST', '/v1/admin/catalog/import', { csv, apply: true })) as ImportReport);
    } catch {
      /* shown by m.error */
    } finally {
      setBusy(false);
    }
  };
  const s = report?.summary;
  const changes = s ? s.created + s.updated + s.newPrices : 0;

  return (
    <Modal title="Importer un tableur" onClose={() => onClose(!!report?.applied)}>
      <p className="muted">
        Fichier CSV (séparateur « ; » ou « , »), par exemple enregistré depuis Excel. Le plus simple : cliquez
        d’abord sur « Exporter », complétez le fichier, puis importez-le. Rien n’est modifié avant votre
        confirmation ; un changement de prix crée une nouvelle version datée.
      </p>
      <Field label="Fichier">
        <input
          type="file"
          accept=".csv,text/csv"
          onChange={async (e) => {
            const f = e.target.files?.[0];
            if (!f) return;
            const text = await f.text();
            setFileName(f.name);
            setCsv(text);
            await preview(text);
          }}
        />
      </Field>
      <ErrorBox message={m.error} />
      {busy && <p className="muted">Analyse en cours…</p>}
      {report && s && (
        <>
          <p role="status" className={report.applied ? 'badge tone-success' : s.errors ? 'field-error' : ''}>
            {report.applied ? 'Import terminé : ' : `${fileName} : `}
            {s.created} nouveau(x), {s.updated} modifié(s), {s.unchanged} inchangé(s), {s.newPrices}{' '}
            nouveau(x) tarif(s)
            {s.errors > 0 && `, ${s.errors} ligne(s) en erreur à corriger avant import`}
          </p>
          <table className="table">
            <thead>
              <tr>
                <th>Ligne</th>
                <th>Article</th>
                <th>Action</th>
                <th>Détails</th>
              </tr>
            </thead>
            <tbody>
              {report.rows
                .filter(
                  (r) => r.action !== 'unchanged' || r.priceAction === 'new_version' || r.messages.length,
                )
                .map((r) => (
                  <tr key={r.line}>
                    <td>{r.line}</td>
                    <td>{r.label}</td>
                    <td className={r.action === 'error' ? 'field-error' : ''}>
                      {IMPORT_ACTION[r.action]}
                      {r.priceAction === 'new_version' && ' · nouveau tarif'}
                    </td>
                    <td>{r.messages.join(' · ')}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </>
      )}
      <div className="actions">
        <button className="btn" onClick={() => onClose(!!report?.applied)}>
          {report?.applied ? 'Fermer' : 'Annuler'}
        </button>
        {report && !report.applied && (
          <button
            className="btn primary"
            disabled={busy || !csv || !!s?.errors || changes === 0}
            onClick={apply}
          >
            Importer {changes} changement(s)
          </button>
        )}
      </div>
    </Modal>
  );
}

/** Photo de la variante : réduite dans le navigateur (1200 px, JPEG) avant l'envoi. */
async function shrinkImage(file: File): Promise<string> {
  const bmp = await createImageBitmap(file);
  const scale = Math.min(1, 1200 / Math.max(bmp.width, bmp.height));
  const c = document.createElement('canvas');
  c.width = Math.round(bmp.width * scale);
  c.height = Math.round(bmp.height * scale);
  const g = c.getContext('2d')!;
  g.fillStyle = '#ffffff';
  g.fillRect(0, 0, c.width, c.height);
  g.drawImage(bmp, 0, 0, c.width, c.height);
  return c.toDataURL('image/jpeg', 0.85).split(',')[1]!;
}

function ItemImage({ item, onChanged }: { item: CatalogItemRecord; onChanged(): void }) {
  const m = useMutation();
  const [url, setUrl] = useState(item.imageUrl);
  return (
    <div className="doc-image">
      <strong>Image de la variante</strong>
      <div className="doc-image-box">
        {url ? <img src={url} alt={item.label} /> : <span className="muted">Aucune image</span>}
      </div>
      <div className="actions">
        <label className="btn small">
          {url ? 'Remplacer' : 'Choisir une image'}
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            hidden
            aria-label="Image de la variante"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              void shrinkImage(file)
                .then((dataBase64) =>
                  m.run('PUT', `/v1/admin/catalog/items/${item.id}/image`, { dataBase64 }),
                )
                .then(
                  (r) => (setUrl((r as { imageUrl: string }).imageUrl), onChanged()),
                  () => undefined,
                );
            }}
          />
        </label>
        {url && (
          <button
            className="btn small danger-outline"
            onClick={() =>
              void m.run('DELETE', `/v1/admin/catalog/items/${item.id}/image`).then(
                () => (setUrl(null), onChanged()),
                () => undefined,
              )
            }
          >
            Retirer
          </button>
        )}
      </div>
      <ErrorBox message={m.error} />
    </div>
  );
}
